// src/lib/facturation-lignes.js — WAGON M (Lot M3.3, F-M3-4)
//
// Construit le SNAPSHOT structuré des lignes facturées d'une facture commission
// proprio (série P), stocké dans `invoices.meta.lignes` à l'émission.
//
// Pourquoi un snapshot structuré (en plus du html_snapshot existant) :
//   - le html_snapshot fige le RENDU (CGI 289) mais n'est pas requêtable ;
//   - meta.lignes fige les DONNÉES (audit, réconciliation, futur diff
//     KV↔V2) sous forme exploitable. Ajout PROSPECTIF : aucune facture
//     P-2026 existante n'est touchée, aucune numérotation ni HTML modifié.
//
// PUR : aucune dépendance Supabase/React. Mêmes conventions de calcul commission
// que facture-html.js (base = montant − ménage ; taux dégressif par palier) et
// que calc.js (getResaTaux). On NE recalcule PAS le total facture ici (le
// montant facturé reste celui de createInvoice) — on décrit chaque ligne résa.
//
// Une ligne = une résa du mois (telle que projetée : KV V1 OU V2 selon la
// source du logement, car l'appelant fournit `resas` = c.resas du wrapper calc
// qui lit déjà le `data` projeté).

import { getResaTaux } from './calc'
import { resoudreCommission } from './commission-taux-date'

// Arrondi 2 décimales. NE MASQUE PLUS les NaN (paquet argent 2026-09-03) :
// l'ancien `Number(n)||0` snapshotait un montant incalculable comme un faux 0.
// null/undefined (résa « sans montant », gérée à l'écran) restent 0 ; un
// non-fini fourni RESTE NaN — visible, jamais un zéro qui ment. Le REFUS
// d'émission vit dans facture-html.js (genFactureHTML), appelé AVANT ce
// snapshot dans les deux flux d'émission.
function round2(n) {
  const v = Number(n ?? 0)
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : NaN
}

/**
 * @param {Array} resas — résas du mois pour le logement (c.resas du wrapper calc).
 * @param {object} log — le logement (taux / commissionMode / paliers / tauxBase).
 * @param {string} [moisKey] — clé mois « AAAA-MM » du bucket facturé. Sert à
 *   résoudre le barème DATÉ (`log.tauxHistorique`). Facultatif tant que le
 *   logement n'est pas historisé — auquel cas le barème plat d'aujourd'hui est
 *   utilisé, exactement comme avant. ⚠️ Jamais un libellé français.
 * @returns {Array<{voyageur, plateforme, nuits, montant, menage, base, commission}>}
 *   base       = max(0, montant − menage)  (base commission, ADR-048)
 *   commission = base × taux applicable / 100  (taux dégressif par palier si configuré)
 */
export function buildFacturationLignes(resas, log, moisKey) {
  if (!Array.isArray(resas)) return []
  // Barème EN VIGUEUR au mois facturé. Non historisé ⇒ `historise` false ⇒
  // `baremeSrc` est le `log` lui-même : chemin d'avant, byte pour byte.
  const bareme = resoudreCommission(log, moisKey)
  const baremeSrc = bareme.historise ? bareme : log
  return resas.filter(Boolean).map((r) => {
    const montant = round2(r.montant)
    const menage = round2(r.menage)
    const base = Math.max(0, round2(montant - menage))
    // Taux applicable : dégressif (par nuits) si configuré, sinon taux fixe log.
    const taux =
      baremeSrc && baremeSrc.commissionMode === 'degressive'
        ? getResaTaux(baremeSrc, r.nuits || 0)
        : baremeSrc && baremeSrc.taux != null
          ? baremeSrc.taux
          : 0
    const commission = round2((base * taux) / 100)
    return {
      voyageur: r.voyageur || '',
      plateforme: r.plateforme || '',
      nuits: r.nuits || 0,
      montant,
      menage,
      base,
      commission,
    }
  })
}
